import React, { useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, navigate } from 'raviger';
import { Button, Card, Col, Modal, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { Tooltip as MapTooltip, Marker } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from 'react-leaflet-cluster';
import Moment from 'react-moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faCircle, faExclamation, faFilePdf, faSearch, faUsers
} from '@fortawesome/free-solid-svg-icons';
import { faCalendarExclamation, faChevronDoubleUp, faMapLocation } from '@fortawesome/pro-regular-svg-icons';
import { faChevronCircleDown, faChevronCircleUp } from '@fortawesome/pro-duotone-svg-icons';
import Map, { CircleMarker, BoundsUpdater, prettyText, createPinMarkerIcon } from "../components/Map";
import Header from "../components/Header";
import Scrollbar from '../components/Scrollbars';
import { SystemErrorContext } from '../components/SystemError';
import { AddressLookup, countDictMatches } from '../components/Map';
import { AuthContext } from "../accounts/AccountsReducer";
import { capitalize } from '../utils/formatString';
import { printDispatchSummary } from './Utils';

function MapLegendControl({setShowAddressModal}) {
  return (
      <div className='leaflet-control float-end map-legend mt-2 me-2'>
          <OverlayTrigger
            key={"address-finder"}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-address-finder`}>
                Search for an address to zoom the map to.
              </Tooltip>
            }
          >
            <Button onClick={() => setShowAddressModal(true)}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </OverlayTrigger>
      </div>
  )
}

function Dispatch({ incident, organization }) {

  const { setShowSystemError } = useContext(SystemErrorContext);
  const { state } = useContext(AuthContext);

  const mapRef = useRef(null);

  const [data, setData] = useState({dispatch_assignments: [], isFetching: true, bounds:L.latLngBounds([[0,0]])});
  const [mapState, setMapState] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showActive, setShowActive] = useState(true);
  const [showPreplanned, setShowPreplanned] = useState(true);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [initialBounds, setInitialBounds] = useState(L.latLngBounds([[0,0]]));
  const [species, setSpecies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const [pinMarkerIcon, setPinMarkerIcon] = useState(createPinMarkerIcon(13));

  const priorityText = {1:'Urgent', 2:'High', 3:'Medium', 4:'Low', 5:'Lowest'};

  const createCustomClusterIcon = (cluster) => {
    const map = mapRef.current
    let zoom = map.getZoom();
    const count = cluster.getChildCount();
    let clusterSizeClass = 'marker-cluster marker-cluster-small';
    if (zoom >= 16 && zoom < 17) {
      clusterSizeClass = 'marker-cluster marker-cluster-medium';
    } else if (zoom >= 17) {
      clusterSizeClass = 'marker-cluster marker-cluster-large';
    }

    return L.divIcon({
      html: `<span>${count}</span>`,
      className: clusterSizeClass, // Add a custom CSS class for styling
      iconSize: [0, 0],
      iconAnchor: [8, 9], // Adjust anchor to center the icon
    });
  };

  const handleClose = () => {setShowAddressModal(false);}

  const handlePrintDownloadSummary = async (e) => {
    setIsLoading(true);
    printDispatchSummary({
      drs: data.dispatch_assignments, 
      organization: state.organization.name, 
      incident: state.incident.name
      });
    setIsLoading(false);
  }

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchSpecies = () => {
      // Fetch Species data.
      axios.get('/animals/api/species/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let species_options = [];
          response.data.forEach(result => {
            // Build species option list.
            species_options.push({value: result.name, label: capitalize(result.name)});
          });
          setSpecies(response.data);
          fetchDispatchAssignments(response.data);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchSpecies();

    const fetchDispatchAssignments = async (species_data) => {
      setData({dispatch_assignments: [], isFetching: true, bounds:L.latLngBounds([[0,0]])});
      // Fetch open DA data.
      let dispatch_assignments = [];
      if (!unmounted) {
        const map_dict = {};
        const bounds = [];
        const dupe_bounds = new Set();
        let nextUrl = '/evac/api/evacassignment/?page=1&page_size=25';
        do {
          const response = await axios.get(nextUrl, {
            params: {
              status: 'open',
              incident: incident,
              organization: organization,
              map: true
            },
            cancelToken: source.token,
          })
          .catch(error => {
            // setData({dispatch_assignments: [], isFetching: false, bounds:L.latLngBounds([[0,0]])});
            if (error.response) {
              setShowSystemError(true);
            }
          });

          if (response) {
            response.data.results.forEach((dispatch_assignment, index) => {
              let sr_dict = {}
              for (const assigned_request of dispatch_assignment.assigned_requests) {
                const matches = countDictMatches(assigned_request.animals, species_data);
                sr_dict[assigned_request.service_request_object.id] = {id:assigned_request.service_request_object.id, matches:matches, latitude:assigned_request.service_request_object.latitude, longitude:assigned_request.service_request_object.longitude, full_address:assigned_request.service_request_object.full_address};
                const key = `${assigned_request.service_request_object.latitude},${assigned_request.service_request_object.longitude}`;
                if (!dupe_bounds.has(key)) {
                  dupe_bounds.add(key);
                  bounds.push([assigned_request.service_request_object.latitude, assigned_request.service_request_object.longitude]);
                }
              }
              map_dict[dispatch_assignment.id] = {service_requests:sr_dict};
            });
            dispatch_assignments.push(...response.data.results);
            nextUrl = response.data.next;
              if (nextUrl) {
                nextUrl = '/evac/' + response.data.next.split('/evac/')[1];
              }
          }
          else {
            nextUrl = null;
          }
        } while(nextUrl != null)

        setMapState(map_dict);

        // Use the Incident bounds if we don't have any DAs.
        if (dispatch_assignments.length === 0) {
          bounds.push([parseFloat(state.incident.coordinates[0])+.04,parseFloat(state.incident.coordinates[1])-.04]);
          bounds.push([parseFloat(state.incident.coordinates[0])-.04,parseFloat(state.incident.coordinates[1])+.04]);
        }
        setInitialBounds(bounds);
        setData({dispatch_assignments: dispatch_assignments.sort((a, b) => a.team_name.localeCompare(b.team_name)), isFetching: false, bounds:bounds.length > 0 ? bounds : L.latLngBounds([[0,0]])});
      }
    };

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [incident]);

  return (
    <>
    <Header>Field
    {/* {isSubscribed ?
    <OverlayTrigger
      key={"unsubscribe"}
      placement="bottom"
      overlay={
        <Tooltip id={`tooltip-unsubscribe`}>
          You are currently subscribed. Click to unsubscribe to stop receiving email notifications when Field Assignments are created for this incident.
        </Tooltip>
      }
    >
      <Button className="ms-1" onClick={() => subscribe()} style={{marginTop:"-8px", background:"#a72b46"}}>
        <FontAwesomeIcon size="lg" icon={faBellSlash} />
      </Button>
    </OverlayTrigger>
    :
    <OverlayTrigger
      key={"subscribe"}
      placement="bottom"
      overlay={
        <Tooltip id={`tooltip-subscribe`}>
          You are currently unsubscribed. Click to subscribe to receive email notifications when Field Assignments are created for this incident.
        </Tooltip>
      }
    >
      <Button className="ms-1" onClick={() => subscribe()} style={{marginTop:"-8px", background:"#365a7d"}}>
        <FontAwesomeIcon size="lg" icon={faBell} />
      </Button>
    </OverlayTrigger>
    } */}
    <OverlayTrigger
      key={"download-active"}
      placement="bottom"
      overlay={
        <Tooltip id={`tooltip-download-active`}>
          Download a brief summary for all open Field Assignments.
        </Tooltip>
      }
    >
    {isLoading ? (
        <Spinner
          {...{
            as: 'span',
            animation: 'border',
            size: undefined,
            role: 'status',
            'aria-hidden': 'true',
            variant: 'light',
            style: {
              height: '2rem',
              width: '2rem',
              marginBottom: '0.25rem',
              marginLeft: '0.5rem'
            }
          }}
        />
    ) : (  
      <Button className="ms-1" onClick={() => handlePrintDownloadSummary()} style={{marginTop:"-8px", background:"#365a7d"}}>
        <FontAwesomeIcon size="lg" icon={faFilePdf} />
      </Button>
      )
    }
    </OverlayTrigger>
    </Header>
    <Row className="ms-0 me-0 ps-0 pe-0 mb-0">
      <Col xs={10} className="ps-0 pe-0">
        <Link href={"/" + organization + "/" + incident + "/field/deploy"} style={{textDecoration:"none"}}>
          <Button className="rounded border btn-block w-100" style={{height:"100px", fontSize:"20px"}}><FontAwesomeIcon icon={faMapLocation} className="me-1 fa-move-up" inverse />DEPLOY TEAMS</Button>
        </Link>
      </Col>
    </Row>
    <Row xs={12} className="ms-0 me-0 ps-0 pe-0" style={{marginBottom:"-1px"}}>
      <Col xs={10} className="border rounded ps-0 pe-0">
        {!data.isFetching && data.bounds.length > 0 ?
          <Map ref={mapRef} bounds={data.bounds.length > 1 ? data.bounds : null} center={data.bounds.length === 1 ? [data.bounds[0][0], data.bounds[0][1]] : null} boundsOptions={{padding:[10,10]}} className="landing-leaflet-container" manual_spider={true} setPinMarkerIcon={setPinMarkerIcon}>
            <MapLegendControl setShowAddressModal={setShowAddressModal} />
            <BoundsUpdater bounds={data.bounds} />
            {data.dispatch_assignments.filter(da => da.id === highlight).map((dispatch_assignment, da_index) => (
            <span key={dispatch_assignment.id}>
              {dispatch_assignment.assigned_requests.map((assigned_request, index) => (
                <Marker
                  key={assigned_request.service_request_object.id}
                  position={[assigned_request.service_request_object.latitude, assigned_request.service_request_object.longitude]}
                  hidden={dispatch_assignment.id !== highlight}
                  icon={pinMarkerIcon}
                />
              ))}
            </span>))}
            <MarkerClusterGroup maxClusterRadius={0} spiderfyDistanceMultiplier={0.9} spiderfyOnMaxZoom={true} animate={true} iconCreateFunction={(cluster) => createCustomClusterIcon(cluster)} chunkedLoading>
            {data.dispatch_assignments.map((dispatch_assignment, da_index) => (
            <span key={dispatch_assignment.id}>
              {dispatch_assignment.assigned_requests.map((assigned_request, index) => (
                <span key={assigned_request.id}>
                {!((!showActive || dispatch_assignment.team_member_names.length === 0) && (!showPreplanned || dispatch_assignment.team_member_names.length > 0)) ?
                <CircleMarker
                  key={assigned_request.service_request_object.id}
                  ref={mapRef}
                  center={{lat:assigned_request.service_request_object.latitude, lng: assigned_request.service_request_object.longitude}}
                  weight={highlight === dispatch_assignment.id ? "60" : "30"}
                  fillColor={dispatch_assignment.color}
                  eventHandlers={{
                    click() {
                      navigate('/' + organization + "/" + incident + "/field/summary/" + dispatch_assignment.id_for_incident);
                    },
                  }}
                >
                  <MapTooltip key={`${index}-${selectedTeam}`} direction={"auto"} autoPan={false} closeButton={true}>
                    <span>
                      <div>
                        <span>
                          FA#{dispatch_assignment.id_for_incident}
                          {dispatch_assignment.team_name?.trim() && ` - ${dispatch_assignment.team_name}`}
                        </span>
                      </div>
                      <div>SR#{assigned_request.service_request_object.id_for_incident}: {assigned_request.service_request_object.full_address}</div>
                      {assigned_request.service_request_object.priority ? <div style={{ color: assigned_request.service_request_object.priority === 1 ? "#ff4c4c" : "black"}}>Priority: {priorityText[assigned_request.service_request_object.priority]}</div>: ""}
                      {assigned_request.service_request_object.followup_date ? <div>Followup Date: <Moment format="L">{assigned_request.service_request_object.followup_date}</Moment></div> : ""}
                      {mapState[dispatch_assignment.id] ?
                        <span>
                          {mapState[dispatch_assignment.id] && Object.keys(mapState[dispatch_assignment.id].service_requests[assigned_request.service_request_object.id].matches).length > 0 ? Object.keys(mapState[dispatch_assignment.id].service_requests[assigned_request.service_request_object.id].matches).map((key,i) => (
                            <span key={key} style={{textTransform:"capitalize"}}>
                              {i > 0 && ", "}{prettyText(key.split(',')[0], mapState[dispatch_assignment.id].service_requests[assigned_request.service_request_object.id].matches[key], species)}
                            </span>
                          )) : ""}
                        </span>
                      :""}
                      {Object.keys(assigned_request.animals).length === 0 ? "Support SR" : ""}
                      {/* <div>
                        {assigned_request.service_request_object.aco_required ? <img width={16} height={16} src="/static/images/badge-sheriff.png" alt="Special Handling Required" className="me-1" /> : ""}
                        {assigned_request.service_request_object.injured ? <img width={16} height={16} src="/static/images/band-aid-solid.png" alt="Injured" className="me-1" /> : ""}
                        {assigned_request.service_request_object.accessible ? <img width={16} height={16} src="/static/images/car-solid.png" alt="Accessible" className="me-1" /> : <img width={16} height={16} src="/static/images/car-ban-solid.png" alt="Not Acessible" className="me-1" />}
                        {assigned_request.service_request_object.turn_around ? <img width={16} height={16} src="/static/images/trailer-solid.png" alt="Turn Around" /> : <img width={16} height={16} src="/static/images/trailer-ban-solid.png" alt="No Turn Around" className="me-1" />}
                      </div> */}
                    </span>
                  </MapTooltip>
                </CircleMarker> : ""}
                </span>
              ))}
            </span>
            ))}
            </MarkerClusterGroup>
          </Map>
        :
          <Card className="text-center" style={{height:"450px", marginRight:"-1px", paddingTop:"225px", fontSize:"30px"}}>Fetching Field Assignments...</Card>
        }
      </Col>
      <Col xs={2} className="ms-0 me-0 ps-0 pe-0 border rounded">
        <Scrollbar no_shadow="true" style={{height:"450px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
        <Button variant={"info"} className="border" onClick={() => setShowActive(!showActive)} style={{maxHeight:"36px", width:"100%", marginTop:"-1px"}}>Active ({data.dispatch_assignments.filter(da => (da.team_member_names.length)).length}) {showActive ? <FontAwesomeIcon icon={faChevronCircleUp} size="sm" /> : <FontAwesomeIcon icon={faChevronCircleDown} size="sm" />}</Button>
        {data.dispatch_assignments.map((dispatch_assignment, da_index) => (
          <Button key={dispatch_assignment.id} title={dispatch_assignment.team ? dispatch_assignment.team.name : ""} hidden={!showActive || dispatch_assignment.team_member_names.length === 0} variant={dispatch_assignment.id === selectedTeam ? "primary" : "secondary"} className="border" onClick={() => navigate('/' + organization + "/" + incident + "/field/summary/" + dispatch_assignment.id_for_incident)} style={{maxHeight:"36px", width:"100%", marginTop:"-1px", textAlign:"left", fontSize:"14px", paddingLeft:"8px", paddingRight:"0px"}} onMouseEnter={() => {setHighlight(dispatch_assignment.id)}} onMouseLeave={ () => {setHighlight(null)}}>
            <FontAwesomeIcon icon={faCircle} className="me-1" style={{color:dispatch_assignment.color, marginLeft:"-1px"}} />
            <span>FA#{dispatch_assignment.id_for_incident}</span>
            {dispatch_assignment.team_member_names ?
              <OverlayTrigger
                key={"team-names"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-team-names`}>
                    {dispatch_assignment.team_name ? <div>{dispatch_assignment.team_name}</div> : ""}{dispatch_assignment.team_member_names}
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faUsers} className="ms-1" />
              </OverlayTrigger>
            : ""}
            { dispatch_assignment.urgent ? 
              <OverlayTrigger
                key={"da-urgent"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-da-urgent`}>
                    At least one Service Request has an Urgent Priority in this Field Assignment.
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faChevronDoubleUp} style={{ color: "#ff4c4c" }} className="ms-1" />
              </OverlayTrigger>
            : ""}
            { dispatch_assignment.overdue ? 
              <OverlayTrigger
                key={"da-overdue"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-da-overdue`}>
                    At least one Service Request is currently overdue in this Field Assignment.
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faCalendarExclamation} style={{ color: "#ff4c4c" }} className="ms-1" />
              </OverlayTrigger>
            : ""}
            <span className="float-end" style={{marginTop:"-2px"}}>
              {dispatch_assignment.assigned_requests.filter(assigned_request => assigned_request.visit_note && assigned_request.visit_note.date_completed).length === dispatch_assignment.assigned_requests.length ?
                <OverlayTrigger
                  key={"request-complete"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-request-complete`}>
                      Field Assignment visits have all been marked as completed.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faCheck} className="me-1" size="2x" transform={'shrink-4'} inverse />
                </OverlayTrigger>
                :
                <OverlayTrigger
                  key={"request-pending"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-request-pending`}>
                      Field Assignment visits are still pending.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faExclamation} style={{marginRight:"12px"}} size="2x" transform={'shrink-4'} inverse />
                </OverlayTrigger>
                }
              </span>
          </Button>
        ))}
        {/* </MarkerClusterGroup> */}
        {data.dispatch_assignments.filter(da => (da.team_member_names.length)).length === 0 ? <Button className="border" style={{maxHeight:"36px", width:"100%", marginTop:"-1px", backgroundColor:"rgb(128, 128, 128)", cursor:"default"}}>None</Button> : ""}
        <Button variant={"info"} className="border" onClick={() => setShowPreplanned(!showPreplanned)} style={{maxHeight:"36px", width:"100%", marginTop:"-1px"}}>Preplanned ({data.dispatch_assignments.filter(da => (da.team_member_names.length === 0)).length}) {showPreplanned ? <FontAwesomeIcon icon={faChevronCircleUp} size="sm" /> : <FontAwesomeIcon icon={faChevronCircleDown} size="sm" />}</Button>
        {data.dispatch_assignments.map((dispatch_assignment, da_index) => (
          <Button key={dispatch_assignment.id} title={dispatch_assignment.team ? dispatch_assignment.team.name : ""} hidden={!showPreplanned || dispatch_assignment.team_member_names.length > 0} variant={dispatch_assignment.id === selectedTeam ? "primary" : "secondary"} className="border" onClick={() => navigate('/' + organization + "/" + incident + "/field/summary/" + dispatch_assignment.id_for_incident)} style={{maxHeight:"36px", width:"100%", marginTop:"-1px", textAlign:"left", fontSize:"14px", paddingLeft:"8px", paddingRight:"0px"}} onMouseEnter={() => {setHighlight(dispatch_assignment.id)}} onMouseLeave={ () => {setHighlight(null)}}>
            <FontAwesomeIcon icon={faCircle} className="me-1" style={{color:dispatch_assignment.color, marginLeft:"-1px"}} />
            FA#{dispatch_assignment.id_for_incident}
            {dispatch_assignment.team_member_names ?
              <OverlayTrigger
                key={"team-names"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-team-names`}>
                    {dispatch_assignment.team_member_names}
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faUsers} className="ms-1" />
              </OverlayTrigger>
            : ""}
          </Button>
        ))}
        {data.dispatch_assignments.filter(da => (da.team_member_names.length === 0)).length === 0 ? <Button className="border" style={{maxHeight:"36px", width:"100%", marginTop:"-1px", backgroundColor:"rgb(128, 128, 128)", cursor:"default"}}>None</Button> : ""}
        </Scrollbar>
      </Col>
    </Row>
    <Row className="ms-0 me-0 border rounded" style={{maxHeight:"38px"}}>
      <h5 className="card-header d-flex align-items-center" style={{height:"36px", width:"100%", backgroundColor:"#808080"}}>
        Active/Preplanned Field Assignments
      </h5>
    </Row>
    <Modal show={showAddressModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Address Finder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AddressLookup state={state} style={{width: '100%'}} className={"form-control"} setData={setData} initialBounds={initialBounds} incident={incident} handleClose={handleClose} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Close</Button>
      </Modal.Footer>
    </Modal>
    </>
  )
}

export default Dispatch
